import parse from 'html-react-parser';
import styled from 'styled-components';

import { RATE_IT_TIER_ART } from '../assets';

/**
 * A raster glyph tinted the way `tintColor` tints one on mobile.
 *
 * The asset is a white alpha mask, so the colour comes from `background-color` showing through
 * the mask rather than from the image itself. `filter: brightness(0) invert(1)` was the obvious
 * alternative and is rejected: it can only ever produce white, so the first tier that wants a
 * tinted glyph would break it silently.
 */
const RasterGlyph = styled.span`
  display: inline-block;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  background-color: ${(props) => props.$col};
  -webkit-mask: url(${(props) => props.$src}) center / contain no-repeat;
  mask: url(${(props) => props.$src}) center / contain no-repeat;
  flex: none;
`;

const VectorGlyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  line-height: 0;
`;

/**
 * One tier's glyph, at a given height and colour.
 *
 * This file is the ENTIRE seam between the two kinds of artwork Rate It's tiers ship as — two
 * vectors and one raster (see `../assets/index.js` for why Epic is a PNG). Nothing else in the
 * module knows they differ: callers ask for a rating, a size and a colour.
 *
 * Normalised by HEIGHT, not by a square box. The Great glyph is a double thumbs-up on a 48x26
 * viewBox — nearly twice as wide as it is tall — so fitting all three into a square would render
 * it at barely half the height of the others and read as a mistake. This is also why the
 * app-wide `<Icon>` is not used here: its wrapper hard-codes `width === height`.
 */
const RateItTierIcon = ({ rating, size, col }) => {
  const art = RATE_IT_TIER_ART[rating];
  if (!art) return null;

  const width = size * art.aspect;

  if (art.kind === 'raster') {
    return <RasterGlyph $src={art.src} $width={width} $height={size} $col={col} aria-hidden />;
  }

  // The factory derives the SVG's own width from its viewBox; `dimensions` is the height.
  return <VectorGlyph aria-hidden>{parse(art.xml({ dimensions: size, col }))}</VectorGlyph>;
};

export default RateItTierIcon;
